import React, { useMemo, useState } from 'react';
import ReactLoader from 'react-loading';
import { useHistory } from 'react-router-dom';

import moment from 'moment';

import ImportOrAdd from 'components/AddExpenseBottomSheet';
import Header from 'components/AppBar';
import FloatingButton from 'components/FloatingButton';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import ExpensesSelectableList from 'containers/ExpensesSelectableList';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { TravelExpensesResponse } from 'models/expense';
import { getFormattedPrice } from 'utils/currency';

import { Container, Body, Loading, Title, SubTitle, Total, Report } from './styles';

interface Props {
  isLoading: boolean;
  isReportExportLoading: boolean;
  isReportDownloadLoading: boolean;
  travelId: string;
  travel?: TravelExpensesResponse;
  onGenerateReport: () => void;
  onDownloadReport: () => void;
}

const ExpensesList = ({
  isLoading,
  isReportExportLoading,
  travelId,
  travel,
  onGenerateReport,
  onDownloadReport,
  isReportDownloadLoading,
}: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency: travel?.currencyCode, t });
    return formatted;
  };

  const [isShowAdd, setIsShowAdd] = useState(false);

  const isHotel = useMemo(() => travel?.type?.toUpperCase() === TravelType.Hotel.toUpperCase(), [travel]);
  const isVehicle = useMemo(() => travel?.type === TravelType.Vehicle, [travel]);
  const isExtraCharge = useMemo(() => travel?.type === TravelType.ExtraCharge, [travel]);
  const isDesktop = useIsDesktop();

  const price = useMemo(() => {
    if (travel) {
      const { totalValue } = travel;
      return formattedPrice(totalValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleSelect = (id: string) => {
    history.push(`/corporate/expense/${id}`, { id: travelId });
  };

  const handleAdd = () => {
    history.push('/corporate/expense', { id: travelId });
  };

  const handleImport = () => {
    history.push(`/corporate/expensesImport/${travelId}`, { id: travelId });
  };

  const subtitle = useMemo(
    () =>
      t(
        isVehicle
          ? 'expensesList.headerVehicleSubtitle'
          : isHotel
          ? 'expensesList.headerHotelSubtitle'
          : isExtraCharge
          ? 'expensesList.headerExtraChargeSubtitle'
          : 'expensesList.headerFlightSubtitle',
        {
          city: travel?.destinationName,
          day: moment(travel?.checkin).format('DD'),
          month: moment(travel?.checkin).format('MMMM'),
        },
      ),
    [isExtraCharge, isHotel, isVehicle, t, travel],
  );

  return (
    <>
      <Mobile>
        <Header goBack={() => history.push('/corporate/travelsWithExpenses')}>
          <Title>{t('expensesList.title')}</Title>
          {!isLoading && <SubTitle>{subtitle}</SubTitle>}
        </Header>
      </Mobile>

      <Desktop>
        <ScreenHeader title={t('expensesList.title')} subtitle={isLoading ? '' : subtitle} />
      </Desktop>
      <Container>
        <Body>
          {isLoading ? (
            <Loading fullScreen />
          ) : (
            <>
              <Total>
                <Total.Title>{t('expensesList.totalExpenses')}</Total.Title>
                <Total.Value>{price}</Total.Value>
              </Total>
              {isDesktop ? (
                <>
                  <Report onClick={onGenerateReport}>
                    {isReportExportLoading ? (
                      <ReactLoader type="spin" width={26} height={26} />
                    ) : (
                      <>
                        <Report.Text>{t('expensesList.generateReport')}</Report.Text>
                        <Report.ShareIcon src="/corporate/images/share.svg" />
                      </>
                    )}
                  </Report>
                  <Report onClick={onDownloadReport}>
                    {isReportDownloadLoading ? (
                      <ReactLoader type="spin" width={26} height={26} />
                    ) : (
                      <>
                        <Report.Text>{t('expensesList.downloadReport')}</Report.Text>
                        <Report.DownloadIcon src="/corporate/images/download.svg" />
                      </>
                    )}
                  </Report>
                </>
              ) : (
                <Report onClick={onGenerateReport}>
                  {isReportExportLoading ? (
                    <ReactLoader type="spin" width={26} height={26} />
                  ) : (
                    <>
                      <Report.Text>{t('expensesList.generateReport')}</Report.Text>
                      <Report.ShareIcon src="/corporate/images/share.svg" />
                    </>
                  )}
                </Report>
              )}

              <ExpensesSelectableList expenses={travel?.expenses} onSelect={handleSelect} />
            </>
          )}
        </Body>
        <FloatingButton onClick={() => setIsShowAdd(true)} />
        <ImportOrAdd show={isShowAdd} onAdd={handleAdd} onImport={handleImport} onClose={() => setIsShowAdd(false)} />
      </Container>
    </>
  );
};

export default ExpensesList;
